import { useState, useEffect, useRef, useCallback, type MouseEvent } from 'react'
import { XMarkIcon, ArrowDownTrayIcon, CircleStackIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { notify } from '@/utils/notify'
import type { DataSource } from '@/services/api'
import {
  encryptDatasourcesForExport,
  downloadExportFile,
  type DatasourceExportData
} from '@/utils/datasourceExport'

interface ExportConnectionsModalProps {
  isOpen: boolean
  onClose: () => void
  connections: DataSource[]
}

interface ConnectionPasswordEntry {
  connectionId: string
  password: string
}

export function ExportConnectionsModal({ isOpen, onClose, connections }: ExportConnectionsModalProps) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set())
  const [connectionPasswords, setConnectionPasswords] = useState<ConnectionPasswordEntry[]>([])
  const [showConnectionPasswords, setShowConnectionPasswords] = useState<Set<string>>(new Set())
  const [isExporting, setIsExporting] = useState(false)
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string; selection?: string }>({})

  const modalRef = useRef<HTMLDivElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPassword('')
      setConfirmPassword('')
      setShowPassword(false)
      setShowConfirmPassword(false)
      setErrors({})
      // Auto-select all connections that have valid IDs
      setSelectedConnections(new Set(connections.map(c => c.id)))
      // Initialize empty passwords for all connections
      setConnectionPasswords(connections.map(c => ({ connectionId: c.id, password: '' })))
      setShowConnectionPasswords(new Set())
      // Focus password input
      setTimeout(() => {
        passwordInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, connections])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleModalClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
  }, [])

  const toggleConnectionSelection = (connectionId: string) => {
    setSelectedConnections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(connectionId)) {
        newSet.delete(connectionId)
      } else {
        newSet.add(connectionId)
      }
      return newSet
    })
    // Clear selection error when user makes a selection
    if (errors.selection) {
      setErrors(prev => ({ ...prev, selection: undefined }))
    }
  }

  const selectAll = () => {
    setSelectedConnections(new Set(connections.map(c => c.id)))
    if (errors.selection) {
      setErrors(prev => ({ ...prev, selection: undefined }))
    }
  }

  const deselectAll = () => {
    setSelectedConnections(new Set())
  }

  const updateConnectionPassword = (connectionId: string, newPassword: string) => {
    setConnectionPasswords(prev =>
      prev.map(entry =>
        entry.connectionId === connectionId
          ? { ...entry, password: newPassword }
          : entry
      )
    )
  }

  const toggleConnectionPasswordVisibility = (connectionId: string) => {
    setShowConnectionPasswords(prev => {
      const newSet = new Set(prev)
      if (newSet.has(connectionId)) {
        newSet.delete(connectionId)
      } else {
        newSet.add(connectionId)
      }
      return newSet
    })
  }

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (selectedConnections.size === 0) {
      newErrors.selection = 'Please select at least one connection to export'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleExport = async () => {
    if (!validateForm()) {
      return
    }

    setIsExporting(true)

    try {
      // Gather connection data for export
      const datasourcesData: DatasourceExportData[] = []

      for (const connectionId of selectedConnections) {
        const connection = connections.find(c => c.id === connectionId)
        if (!connection) continue

        // Get the password entry for this connection
        const passwordEntry = connectionPasswords.find(p => p.connectionId === connectionId)

        const exportData: DatasourceExportData = {
          displayName: connection.displayName || '',
          databaseType: connection.databaseType,
          host: connection.host || '',
          port: connection.port || 5432,
          username: connection.username || '',
          agentId: connection.agentId,
          defaultDatabase: connection.defaultDatabase,
          connectionParams: connection.connectionParams as Record<string, unknown> | null,
          isLocked: connection.isLocked
        }

        // Include password if provided
        if (passwordEntry && passwordEntry.password) {
          exportData.password = passwordEntry.password
        }

        datasourcesData.push(exportData)
      }

      // Encrypt and create export file
      const encryptedContent = await encryptDatasourcesForExport(datasourcesData, password)

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replaceAll(/[:.]/g, '-').slice(0, 19)
      const filename = `connections-export-${timestamp}.json`

      // Download the file
      downloadExportFile(encryptedContent, filename)

      notify.success(`Successfully exported ${datasourcesData.length} connection(s)`)

      // Clear sensitive data before closing
      setPassword('')
      setConfirmPassword('')
      setConnectionPasswords([])

      onClose()
    } catch (error) {
      console.error('Export failed:', error)
      notify.error('Failed to export connections')
    } finally {
      setIsExporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-modal-title"
        className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
              <ArrowDownTrayIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 id="export-modal-title" className="text-lg font-medium text-gray-900 dark:text-white">
              Export Connections
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
            <p className="flex items-start">
              <CircleStackIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              Export your database connections to a secure, password-protected file for backup or transfer to another computer.
            </p>
          </div>

          {/* Connection Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Connections to Export
              </label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Select All
                </button>
                <span className="text-gray-400">|</span>
                <button
                  type="button"
                  onClick={deselectAll}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
              {connections.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  No connections available
                </div>
              ) : (
                connections.map(connection => {
                  const isSelected = selectedConnections.has(connection.id)
                  const passwordEntry = connectionPasswords.find(p => p.connectionId === connection.id)
                  const showPwd = showConnectionPasswords.has(connection.id)

                  return (
                    <div
                      key={connection.id}
                      className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleConnectionSelection(connection.id)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-0 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <div className="ml-3 flex-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {connection.displayName}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({connection.databaseType})
                          </span>
                        </div>
                      </label>

                      {/* Password input for selected connections */}
                      {isSelected && (
                        <div className="mt-2 ml-7">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Password (optional - for importing later)
                          </label>
                          <div className="relative">
                            <input
                              type={showPwd ? 'text' : 'password'}
                              value={passwordEntry?.password || ''}
                              onChange={(e) => updateConnectionPassword(connection.id, e.target.value)}
                              placeholder="Enter connection password..."
                              className="block w-full px-2 py-1 pr-8 text-sm rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => toggleConnectionPasswordVisibility(connection.id)}
                              className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                            >
                              {showPwd ? <EyeSlashIcon className="w-3 h-3" /> : <EyeIcon className="w-3 h-3" />}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
            {errors.selection && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.selection}</p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {selectedConnections.size} of {connections.length} connections selected
            </p>
          </div>

          {/* Encryption Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Encryption Password
            </label>
            <div className="relative">
              <input
                ref={passwordInputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (errors.password) setErrors(prev => ({ ...prev, password: undefined }))
                }}
                placeholder="Enter a strong password..."
                className={`block w-full px-3 py-2 pr-10 rounded-md border shadow-sm transition-colors focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.password ? 'border-red-300 dark:border-red-600' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined }))
                }}
                placeholder="Confirm your password..."
                className={`block w-full px-3 py-2 pr-10 rounded-md border shadow-sm transition-colors focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.confirmPassword ? 'border-red-300 dark:border-red-600' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-yellow-700 dark:text-yellow-300">
            <p className="font-medium mb-1">Important:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Store the exported file securely</li>
              <li>Remember your encryption password - it cannot be recovered</li>
              <li>The file may contain sensitive credentials</li>
              <li>Connection passwords are optional - you can enter them during import if not provided</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={onClose}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleExport()}
            loading={isExporting}
            disabled={isExporting}
            size="sm"
          >
            Export Connections
          </Button>
        </div>
      </div>
    </div>
  )
}
